export const knowledgeEnTranslations = {
  // Page
  pageTitle: 'Organizational Knowledge',
  pageSubtitle: 'Learn from past experiences to build a better future',

  // Actions
  addSignal: 'Create Agent',
  importFromFile: 'Import from file',
  addYourFirstSignal: 'Add Your First Agent',
  edit: 'Edit',
  editSignal: 'Edit Agent',
  deleteSignal: 'Delete',
  retry: 'Retry',

  // Delete confirmation
  deleteSignalTitle: 'Delete Agent',
  deleteSignalDescription: (text: string) =>
    `Are you sure you want to delete this agent? This action cannot be undone.\n\n"${text.substring(
      0,
      100
    )}${text.length > 100 ? '...' : ''}"`,
  deleteSignalCancel: 'Cancel',
  deleteSignalConfirm: 'Delete',
  deleteSignalDeleting: 'Deleting...',
  deleteSignalSuccess: 'Agent deleted successfully',
  deleteSignalError: 'Failed to delete agent',

  // Create signal
  createSignalTitle: 'Create Agent',
  createSignalDescription: 'Add a new agent to your organizational knowledge',
  createSignalPlaceholder:
    'What did you learn? Describe the agent and why it matters...',
  createSignalCancel: 'Cancel',
  createSignalCreate: 'Create agent',
  createSignalCreating: 'Creating...',
  createSignalSuccess: 'Agent created successfully',
  createSignalError: 'Failed to create agent',

  // Create signal form — breadcrumb
  createSignalBreadcrumb: 'Agents',
  createSignalBreadcrumbNew: 'New agent',

  // Create signal form — sections
  propertiesSection: 'Properties',
  visibilitySection: 'Visibility',
  // Create signal form — severity
  severityLabel: 'Severity',
  severityTooltip: 'How impactful is this agent?',
  severityLow: 'Low',
  severityMedium: 'Medium',
  severityHigh: 'High',
  severityCritical: 'Critical',

  // Create signal form — relevant parties
  relevantPartiesLabel: 'Relevant parties',
  relevantPartiesTooltip: 'People or teams this agent applies to',
  relevantPartiesPlaceholder: 'Add',
  detailNoRelevantParties: 'None',

  // Create signal form — marketplace
  marketplaceLabel: 'Marketplace',
  marketplaceTooltip: 'Control who can see this in the marketplace',
  marketplacePublic: 'Public',
  marketplacePrivate: 'Private',

  // Create signal form — visible to
  visibleToLabel: 'Visible to',
  visibleToTooltip: 'Choose who can view this private agent',
  visibleToPlaceholder: 'Add',
  lockedViewerTooltip: 'This viewer cannot be removed',

  // Create signal form — anonymous
  anonymousLabel: 'Anonymous',
  anonymousTooltip: 'Hide your name as the agent author',
  showMyName: 'Show my name',
  anonymous: 'Anonymous',

  // Create signal form — applicability scope
  applicabilitySection: 'Applicability',
  applicabilityBadge: 'Manager',
  scopeLabel: 'Apply to',
  scopeTooltip: 'Choose who this agent applies to',
  scopeSelf: 'Only me',
  scopeDirectReports: 'Me + direct reports',
  scopeFullHierarchy: 'Me + all subordinates',

  // Create signal form — picker labels
  pickerTeamsGroup: 'Teams',
  pickerPeopleGroup: 'People',
  pickerSearchPlaceholder: 'Search...',
  pickerNoResults: 'No results found',

  // Edit signal
  editSignalTitle: 'Edit Agent',
  editSignalDescription: 'Update the agent text below',
  editSignalPlaceholder: 'Enter agent text...',
  editSignalCancel: 'Cancel',
  editSignalSave: 'Save',
  editSignalSaving: 'Saving...',
  editSignalSuccess: 'Agent updated successfully',
  editSignalError: 'Failed to update agent',

  // Status
  active: 'Active',
  inactive: 'Inactive',
  activate: 'Activate',
  deactivate: 'Deactivate',
  activateSignalError: 'Failed to activate agent',
  deactivateSignalError: 'Failed to deactivate agent',

  // Activate scope dialog
  activateDialogTitle: 'Activate Agent',
  activateDialogDescription: 'Choose who this agent should be activated for',
  scopeCountYou: 'you',
  scopeJustMe: 'Just me',
  scopeJustMeDesc: 'Activate only for yourself',
  scopeDirectReportsActivate: 'Direct reports',
  scopeDirectReportsActivateDesc: 'Activate for you and your direct reports',
  scopeFullHierarchyActivate: 'Full hierarchy',
  scopeFullHierarchyActivateDesc: 'Activate for you and all subordinates',
  activateDialogConfirm: 'Activate',
  activateDialogActivating: 'Activating...',

  // Deactivate confirmation dialog
  deactivateDialogTitle: 'Deactivate Agent',
  deactivateDialogAppliedText: (scope: string, count: number) =>
    `This agent is applied to your ${scope}. Deactivating will unapply it from ${count} ${
      count === 1 ? 'person' : 'people'
    }.`,
  deactivateDialogConfirm: 'Deactivate',
  deactivateDialogDeactivating: 'Deactivating...',
  deactivateDialogCancel: 'Cancel',

  // Applied treatment
  appliedByTooltip: (name: string) => `Applied by ${name}`,
  appliedToScope: (scope: string, count: number) =>
    `Applied to ${scope} (${count} ${count === 1 ? 'person' : 'people'})`,
  appliedToDirectReports: 'direct reports',
  appliedToFullHierarchy: 'full hierarchy',

  // Detail modal — applicability scope section
  detailScopeApply: 'Apply',
  detailScopeApplyPrompt:
    'This agent is active only for you. Want to apply it to your team?',
  detailScopeApplyToTeam: 'Apply this agent to your team:',
  detailScopeAppliedBy: (name: string) => `Applied by ${name}`,
  detailScopeAppliedByYouTo: 'Applied by you to',
  detailScopeMembers: (count: number) =>
    `${count} ${count === 1 ? 'member' : 'members'}`,
  detailScopeOnlyMe: 'Only me',
  detailScopeApplying: 'Applying...',
  detailScopeChanging: 'Changing...',
  detailScopeApplyError: 'Failed to apply agent',
  detailScopeUnapplyError: 'Failed to unapply agent',

  // Filters
  searchPlaceholder: 'Search agents...',
  allSources: 'All Sources',
  sortBy: 'Sort by',
  newestFirst: 'Newest First',
  oldestFirst: 'Oldest First',
  mostRelevant: 'Most Relevant',
  filterByInteractionId: 'Filter by Interaction ID',
  interactionIdPlaceholder: 'Enter interaction ID (optional)',

  // Sources
  ilearn: 'ILearn',
  ineedu: 'INeedU',
  manual: 'Manual',

  // Origin badges
  originDebrief: 'I Learn',
  originFeedback: 'I Need U',
  originManual: 'Manual',
  originExternal: 'External Source',
  originExternalWord: 'Word Document',

  // Origin additional info
  fromInteraction: 'From interaction:',
  fromFile: 'From file:',
  viewDebrief: 'View Interaction',
  viewInteraction: 'View Interaction',

  // States
  noSignalsFound: 'No agents found',
  noSignalsFoundDescription: 'Try adjusting your search or filters',
  noSignalsYet: 'No agents yet',
  noSignalsYetDescription:
    'Start building your organizational knowledge by adding your first agent or creating feedback interactions',
  loadingSignals: 'Loading agents...',
  aiAnalyzingDocuments: 'Analyzing your documents',
  aiExtractingSignals: 'Extracting agents from uploaded files',
  errorLoadingSignals: 'Error loading agents',
  unexpectedError: 'An unexpected error occurred',

  // Signal details
  signalDetails: 'Agent Details',
  viewAndManageSignal: 'View and manage this organizational agent',
  signal: 'Agent',
  source: 'Source',
  from: 'From',
  created: 'Created',
  lastUpdated: 'Last Updated',
  visibility: 'Visibility',
  shared: 'Shared',
  sharedWithCompany: 'Shared with company',
  private: 'Private',
  tags: 'Tags',

  // Issue status labels
  statusTodo: 'Todo',
  statusInProgress: 'In Progress',
  statusDone: 'Done',

  // Issue type labels
  issueTypeGoal: 'Goal',
  issueTypeTask: 'Task',

  // Signal detail modal
  detailBreadcrumb: 'Agents',
  detailBreadcrumbCurrent: 'Agent Detail',
  detailInstalled: (count: number) =>
    count === 1 ? '1 installed' : `${count} installed`,
  detailViewSource: 'View source',
  detailLinkedIssues: 'Your Linked Issues',
  detailNoLinkedIssues: 'No linked issues',
  detailActivity: 'Activity',
  detailCreatedSignal: 'created the agent',
  detailUpdatedSignal: 'updated the agent',
  detailRemove: 'Remove',
  detailDeleteSignal: 'Delete agent',
  detailAuthor: 'Author',
  detailUnknownTeam: 'Unknown team',

  // Related issues
  relatedIssues: (count: number) =>
    count === 1 ? '1 related issue' : `${count} related issues`,

  // Count
  signalsFound: (count: number) => `${count} agents found`,
  signalCount: (count: number) => (count === 1 ? '1 agent' : `${count} agents`),
  viewingFilteredSignals: (count: number) =>
    count === 1
      ? 'Viewing 1 filtered agent'
      : `Viewing ${count} filtered agents`,
  viewAllSignals: 'View All Agents',
  noAccessibleSignalsForIds: 'No accessible agents found for the provided IDs.',

  // Import from document (AI suggestions)
  importFromWord: 'Import from document',
  importingFile: 'Importing file...',
  importingDocument: 'Analyzing document and extracting agents...',
  importFileInvalidType: 'Only Word files are allowed (.docx/.doc)',
  importFileLimitExceeded: (max: number) =>
    `You can upload up to ${max} files at a time.`,
  importFileError: 'Failed to process the uploaded document',
  importSuggestionsTitle: 'AI agent suggestions',
  importSuggestionsDescription:
    'Review the AI-generated suggestions from your document and choose which ones to add as agents.',
  extractedTextPreview: 'Extracted text preview',
  noSuggestionsFound: 'No AI suggestions were found for this document',
  selectAll: 'Select all',
  deselectAll: 'Deselect all',
  creatingSignals: 'Creating agents...',
  createSelected: 'Create agents',
  fileNameLabel: 'File name',
  importSuccess: (count: number) =>
    count === 1
      ? '1 agent was created from the document'
      : `${count} agents were created from the document`,

  // Visibility options (legacy — used by upload-signal selector)
  visibilityLabel: 'Agent Visibility',
  visibilityDescription: 'Choose who can see these agents',
  visibilityPrivate: 'Private',
  visibilityPublic: 'Public',
  visibilityPrivateDescription: 'Only visible to you',
  visibilityPublicDescription: 'Visible to all company members',

  // Update feedback
  updateSignalError: 'Failed to update agent',

  // Import modal
  importModal: {
    title: 'Import from Word to Intera',
    description:
      'Upload Word documents to extract agents for your knowledge base',
    reviewDescription:
      'Review and select the agents extracted from your documents',
    browse: 'Browse your files',
    orDragAndDrop: 'or drag and drop here',
    fileTypes: 'Only .doc or .docx files are allowed',
    next: 'Next',
    back: 'Back',
    uploading: 'Uploading files...',
    uploadSuccess: 'Files uploaded successfully',
    done: 'Done',
    duplicateFile: 'File "{{fileName}}" is already selected',
    duplicateFiles: '{{count}} files are already selected',
    extractingSignals: 'Extracting agents from your documents...',
    files: 'files',
    selectedCount: '{{count}} selected',
    save: 'Save',
    cancel: 'Cancel',
    editSignal: 'Edit agent',
    createSignals_one: 'Create {{count}} agent',
    createSignals_other: 'Create {{count}} agents',
    successSingle: '1 agent was created from the document',
    successMultiple: '{{count}} agents were created from the documents',
    uploadedFiles: 'Uploaded Files',
    noFilesYet:
      'No files uploaded yet. Use the drop zone above to add Word documents.',
  },

  // Filter bar
  filterOwner: 'Owner',
  filterSeverity: 'Severity',
  filterStatus: 'Status',
  filterCategory: 'Category',
  sortNewest: 'Newest',
  sortSeverity: 'Severity',
  clearAll: 'Clear all',

  // Category
  categoryCommitment: 'Commitment',
  categoryPlaybook: 'Playbook',
  categoryCommitmentTooltip:
    'A specific commitment between two parties — customer, team, or individual — that may not be honored',
  categoryPlaybookTooltip:
    'A rule, norm, or best practice — org-wide, team, or role — that may not be followed',

  // Informed by
  informedByLabel: 'Informed by',

  // Nudge
  nudgeSuggested: 'Suggested',
  nudgeSkip: 'Skip',

  // Upload CTA
  clickToUpload: 'Upload document',

  // Marketplace
  marketplace: {
    title: 'Agent Marketplace',
    searchPlaceholder: 'Search agents...',
    signalCount: (count: number) =>
      count === 1 ? '1 agent' : `${count} agents`,
    browse: 'Browse',
    myImports: 'My Imports',
    import: 'Import',
    imported: 'Imported',
    remove: 'Remove',
    users: (count: number) => (count === 1 ? '1 user' : `${count} users`),
    topRated: 'TOP RATED',
    anonymous: 'Anonymous',
    browseMarketplace: 'Browse Marketplace',
    noSignalsFound: 'No agents found',
    noImports: "You haven't imported any agents yet",
    noImportsDescription: 'Browse the marketplace to find agents to import',
    importSuccess: 'Agent imported successfully',
    importError: 'Failed to import agent',
    removeSuccess: 'Agent removed successfully',
    removeError: 'Failed to remove agent',
  },

  // Integration sources
  integrations: {
    title: 'Select a Source',
    subtitle: 'Choose an integration to extract agents from your document',
    importFrom: 'Import from source',
    comingSoon: 'Soon',
    comingSoonMessage: (source: string) =>
      `${source} integration is coming soon! Stay tuned.`,
    moreComingSoon: 'More integrations coming soon',
    word: {
      name: 'Word Document',
      description: 'Import agents from .docx or .doc files',
    },
    googleDrive: {
      name: 'Google Drive',
      description: 'Connect to Google Docs and import content',
    },
    confluence: {
      name: 'Confluence',
      description: 'Import pages from your Confluence workspace',
    },
    notion: {
      name: 'Notion',
      description: 'Sync knowledge from your Notion pages',
    },
  },

  // Signal review dialog (import review steps)
  signalReview: {
    title: 'Review Extracted Agents',
    defaultsDescription: 'Set default properties for all {{count}} agents',
    defaultsBanner:
      'These defaults apply to all agents. You can override per agent in the next step.',
    cancel: 'Cancel',
    acceptAllDefaults: 'Accept all with defaults',
    reviewIndividually: 'Review individually',
    reviewSignals: 'Review Agents',
    editSignal: 'Edit Agent',
    signalLabel: 'Agent',
    stepCounter: '{{current}} of {{total}}',
    back: 'Back',
    skipSignal: 'Dismiss agent',
    acceptRemaining: 'Accept remaining ({{count}})',
    next: 'Next',
    finish: 'Finish',
    saveAndBack: 'Save & back to summary',
    createSignals: 'Create Agents',
    summaryDescription: 'Review the agents below before creating them.',
    edited: 'Edited',
    selectedCount: '{{included}} of {{total}} agents selected',
    backToDefaults: 'Back to defaults',
    restore: 'Restore',
    anonymous: 'Anonymous',
    notAnonymous: 'Not anonymous',
  },
};
